package com.siriusxm.pia.views.account

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.Application
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.GlobalAggregatorLoader
import com.siriusxm.unifiedcontent.console.subscriber.SubscriberAccountControls
import unifiedcontent.aggregator.AggregatedPermissions
import unifiedcontent.aggregator.ServiceType
import unifiedcontent.aggregator.ServiceUserMembership
import unifiedcontent.aggregator.ServiceUserRole
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Full user account page displaying the user's profile information, Cognito group
 * permissions, and producer/consumer service memberships. Provides links to
 * navigate to individual producer and consumer detail pages.
 *
 * The page is organized into:
 * - A profile box with basic user information (email, username, roles)
 * - A producers box listing producer service memberships with roles
 * - A consumers box listing consumer service memberships with roles
 */
@Composable
internal fun UserAccountPage() {
    val viewer = Application.viewer
    val userRoles by Application.aggregatorApp.userRoles()

    serviceView {
        serviceContentView {
            entityView({
                title = viewer.email?.substringBefore("@") ?: "User Account"
                subTitle = "User Account"
            }) {
                // Stack the account sections with consistent spacing. The boxes no longer carry
                // their own bottom margin, so the gap is owned by this container.
                Column(gap = 20.px) {
                    // Profile information
                    box("Profile") {
                        detailGrid {
                            detail("Username", viewer.email?.substringBefore("@"))
                            detail("Email", viewer.email)
                            detail("Roles") {
                                UserGroupBadges(showIcons = true, showExtraGroups = true)
                            }
                        }
                    }

                    // Producers
                    val roles = userRoles
                    box({
                        title = "Producers"
                        paddedContent = false
                    }) {
                        if (roles == null) {
                            boxSpinner("Loading producer memberships...")
                        } else {
                            val producers = roles.serviceMemberships()
                                .filter { it.type == ServiceType.PRODUCER }
                            if (producers.isEmpty()) {
                                boxMessage("You are not a member of any producer.")
                            } else {
                                MembershipTable(producers)
                            }
                        }
                    }

                    // Consumers
                    box({
                        title = "Consumers"
                        paddedContent = false
                    }) {
                        if (roles == null) {
                            boxSpinner("Loading consumer memberships...")
                        } else {
                            val consumers = roles.serviceMemberships()
                                .filter { it.type == ServiceType.CONSUMER }
                            if (consumers.isEmpty()) {
                                boxMessage("You are not a member of any consumer.")
                            } else {
                                MembershipTable(consumers)
                            }
                        }
                    }

                    // Permissions
                    val permissions = roles?.permissions()
                    if (permissions != null) {
                        PermissionsSection(permissions)
                    }

                    SubscriberAccountControls()
                }
            }
        }
    }
}

/**
 * Displays a list of service user memberships (producer or consumer).
 * Each row shows the service name linked to its detail page, with an ADMIN
 * badge inline if the user has the admin role for that service.
 *
 * @param memberships List of service memberships to display
 */
@Composable
private fun MembershipTable(memberships: List<ServiceUserMembership>) {
    table<ServiceUserMembership> {
        items(memberships)

        column {
            content { membership ->
                val encodedId = encodeURIComponent(membership.serviceId)
                val path = when (membership.type) {
                    ServiceType.PRODUCER -> "aggregator/producers/$encodedId"
                    ServiceType.CONSUMER -> "aggregator/consumers/$encodedId"
                    else -> "aggregator"
                }
                Div({ classes(AccountStyles.badgeRow) }) {
                    A(href = "#$path", {
                        onClick {
                            it.preventDefault()
                            Application.navigate(path)
                        }
                    }) {
                        Text(membership.name.ifBlank { membership.serviceId })
                    }
                    if (membership.user.roles?.contains(ServiceUserRole.ADMIN) == true) {
                        Badge("ADMIN", BadgeColor.GREEN)
                    }
                }
            }
        }
    }
}

/**
 * Displays the user's aggregated permissions across all service memberships.
 * Shows entity type publishing permissions (with base/partial indicators) and
 * relationship management permissions.
 *
 * @param permissions The aggregated permissions from the /me/permissions endpoint
 */
@Composable
private fun PermissionsSection(permissions: AggregatedPermissions) {
    val types = permissions.types.orEmpty()
    val relationships = permissions.relationships.orEmpty()
    val aggregator = GlobalAggregatorLoader.value()

    if (types.isNotEmpty()) {
        box({
            title = "Permissions"
            paddedContent = false
        }) {
            table<unifiedcontent.aggregator.TypePermission> {
                items(types.sortedWith(compareByDescending<unifiedcontent.aggregator.TypePermission> { it.base == true }.thenBy { it.type }))

                column("Entity Type") {
                    content { perm ->
                        val displayName = aggregator?.entityType(perm.type)?.name?.takeIf { it.isNotBlank() } ?: perm.type
                        A(href = "#aggregator/types/entities/${encodeURIComponent(perm.type)}", {
                            onClick {
                                it.preventDefault()
                                Application.navigate("aggregator/types/entities/${encodeURIComponent(perm.type)}")
                            }
                        }) {
                            Text(displayName)
                        }
                    }
                }

                column("Full Updates") {
                    width = 150.px
                    content { perm ->
                        if (perm.base == true) {
                            Badge("Yes", BadgeColor.GREEN)
                        } else {
                            Badge("No", BadgeColor.GRAY)
                        }
                    }
                }

                column("Partials") {
                    content { perm ->
                        val partials = perm.partials.orEmpty()
                        if (partials.isNotEmpty()) {
                            Div({ classes(AccountStyles.badgeRow) }) {
                                partials.forEach { partialId ->
                                    val partialName = aggregator?.partial(partialId)?.name?.takeIf { it.isNotBlank() } ?: partialId
                                    val path = "aggregator/types/partials/$partialId"
                                    A(href = "#$path", {
                                        onClick {
                                            it.preventDefault()
                                            Application.navigate(path)
                                        }
                                    }) {
                                        Badge(partialName, BadgeColor.BLUE)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    if (relationships.isNotEmpty()) {
        box({
            title = "Relationship Permissions"
            paddedContent = false
        }) {
            table<unifiedcontent.aggregator.RelationshipPermission> {
                items(relationships.sortedBy { it.relationship.id })

                column("Relationship") {
                    content { perm ->
                        Text(perm.relationship.name ?: perm.relationship.id)
                    }
                }

                column("ID") {
                    content { perm ->
                        Text(perm.relationship.id)
                    }
                }
            }
        }
    }
}

/**
 * Shared styles for the user account page and related components (group badges,
 * permissions tables, membership tables).
 */
object AccountStyles : StyleSheet() {
    // Badge layout
    val badgeRow by style {
        display(DisplayStyle.Flex)
        flexWrap(FlexWrap.Wrap)
        alignItems(AlignItems.Center)
        gap((0.375).cssRem)
    }

    val pageGroupRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap((0.5).cssRem)
    }

}
